/**
 * Question Renderer Component
 * Reusable component for rendering questions using Nagwa Questions Engine
 */

import { useEffect, useRef, useState } from "react";
import { globalConfig } from "../../config/globalConfig";
import { useResources } from "../../hooks/useResources";
import type {
  QuestionRendererProps,
  QuestionEngineMessage,
  QuestionEnginePayload,
  QuestionMetadata,
} from "../../interfaces/componentInterfaces/questionRenderer";
import "./QuestionRenderer.scss";

function extractMetadata(questionJson: Record<string, unknown>): QuestionMetadata {
  const parts = (questionJson.content as Record<string, unknown>)?.parts as Array<Record<string, unknown>> | undefined;
  const types = parts
    ? [...new Set(parts.map((p) => String(p.type || "").toUpperCase()).filter(Boolean))].join(", ")
    : "";

  return {
    parentId: String(questionJson.parent_id ?? ""),
    country: String(questionJson.country ?? ""),
    subject: String(questionJson.subject ?? ""),
    grade: String(questionJson.grade ?? ""),
    sectionId: String(questionJson.section_id ?? ""),
    language: String(questionJson.language ?? ""),
    questionTypes: types,
  };
}

const QuestionRenderer = ({
  questionId,
  jobId,
  onRenderComplete,
  onRenderError,
  onMetadataLoaded,
  className = "",
}: QuestionRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isMountedRef = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getResourceValue } = useResources();
  
  // Store callbacks in refs to avoid dependency changes
  const onRenderCompleteRef = useRef(onRenderComplete);
  const onRenderErrorRef = useRef(onRenderError);
  const onMetadataLoadedRef = useRef(onMetadataLoaded);
  
  useEffect(() => {
    onRenderCompleteRef.current = onRenderComplete;
    onRenderErrorRef.current = onRenderError;
    onMetadataLoadedRef.current = onMetadataLoaded;
  }, [onRenderComplete, onRenderError, onMetadataLoaded]);

  useEffect(() => {
    isMountedRef.current = true;
    
    // Reset loading state for new question
    setIsLoading(true);
    setError(null);
    
    // Clean up any existing iframe before rendering new question
    if (iframeRef.current && containerRef.current) {
      try {
        containerRef.current.removeChild(iframeRef.current);
      } catch (_) {
        // Ignore if already removed
      }
      iframeRef.current = null;
    }

    const handleMessage = (event: MessageEvent) => {
      if (!containerRef.current) return;

      try {
        const data: QuestionEngineMessage = JSON.parse(event.data);
        const messageKey = data.messageKey;

        // Handle question rendered and height changed events
        if (["questionRendered", "questionHeightChanged"].includes(messageKey)) {
          if (data.questionId === questionId && data.questionHeight) {
            const newHeight = data.questionHeight;
            if (containerRef.current) {
              containerRef.current.style.height = `${newHeight}px`;
            }
          }

          // Call success callback on first render
          if (messageKey === "questionRendered") {
            setIsLoading(false);
            onRenderCompleteRef.current?.();
          }
        }
      } catch (_) {
        // Ignore parse errors from other messages
      }
    };

    window.addEventListener("message", handleMessage);

    const renderQuestion = async () => {
      if (!containerRef.current || !isMountedRef.current) return;

      // Check if iframe already exists to prevent duplicate rendering
      if (iframeRef.current) {
        return;
      }

      try {
        const engineURL = globalConfig.questionsEngineUrl;
        const baseURL = jobId 
          ? globalConfig.jobQuestionsBaseURL 
          : globalConfig.questionsBaseURL;
        
        // Construct path based on whether we have a jobId
        const questionBasePath = jobId
          ? `${baseURL}/${jobId}/questions/${questionId}/files`
          : `${baseURL}/${questionId}`;

        // Fetch question JSON without credentials to avoid CORS issues with wildcard origin
        const questionJsonPromise = fetch(`${questionBasePath}/${questionId}.json`);

        // Create iframe
        const iframe = document.createElement("iframe");
        iframeRef.current = iframe;

        iframe.onload = async () => {
          if (!isMountedRef.current) return;

          try {
            const questionJson = await questionJsonPromise;
            
            if (!questionJson.ok) {
              if (questionJson.status === 403 || questionJson.status === 404) {
                throw new Error(`Question not found: ${questionJson.status}`);
              }
              throw new Error(`Failed to fetch question: ${questionJson.status}`);
            }

            const questionJsonData = await questionJson.json();

            const metadata = extractMetadata(questionJsonData);
            onMetadataLoadedRef.current?.(metadata);

            const payload: QuestionEnginePayload = {
              action: "init",
              payload: {
                question: questionJsonData,
                assetsBasePath: questionBasePath,
                mode: "session_tutor",
              },
            };

            iframe.contentWindow?.postMessage(JSON.stringify(payload), "*");
          } catch (err) {
            console.error("Error loading question:", err);
            if (isMountedRef.current) {
              // Check if the error is a 403/404 (question not found)
              const isNotFoundError = (err as Error).message?.includes("Question not found");
              const errorMessage = isNotFoundError
                ? getResourceValue(
                    "question_not_found",
                    "Question not found"
                  )
                : getResourceValue(
                    "failed_load_question",
                    "Failed to load question. Please try again."
                  );
              setError(errorMessage);
              setIsLoading(false);
              onRenderErrorRef.current?.(err as Error);
            }
          }
        };

        iframe.onerror = () => {
          if (!isMountedRef.current) return;
          const errorMessage = getResourceValue(
            "failed_load_engine",
            "Failed to load question engine."
          );
          setError(errorMessage);
          setIsLoading(false);
          onRenderErrorRef.current?.(new Error("Failed to load question engine"));
        };

        iframe.src = engineURL;
        iframe.style.width = "100%";
        iframe.style.border = "none";

        if (containerRef.current) {
          containerRef.current.appendChild(iframe);
        }
      } catch (err) {
        console.error("Error rendering question:", err);
        if (isMountedRef.current) {
          const errorMessage = getResourceValue(
            "failed_render_question",
            "Failed to render question. Please try again."
          );
          setError(errorMessage);
          setIsLoading(false);
          onRenderErrorRef.current?.(err as Error);
        }
      }
    };

    renderQuestion();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("message", handleMessage);
      
      // Clean up iframe when component unmounts or questionId/jobId changes
      if (iframeRef.current && containerRef.current) {
        try {
          containerRef.current.removeChild(iframeRef.current);
        } catch (_) {
          // Ignore if already removed
        }
        iframeRef.current = null;
      }
    };
  }, [questionId, jobId, getResourceValue]);

  return (
    <div
      ref={containerRef}
      className={`question-renderer ${className}`}
      data-question-id={questionId}
    >
      {isLoading && (
        <div className="question-renderer__loading">
          <div className="question-renderer__spinner"></div>
          <p>{getResourceValue("loading_question", "Loading question...")}</p>
        </div>
      )}
      {error && (
        <div className="question-renderer__error">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default QuestionRenderer;
